import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import type { Decision } from "../../api/access/decisions";
import { requirementOf } from "../feedback/requirement";
import AnchoredTooltip from "../overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../overlays/useAnchoredTooltip";
import { classNames } from "../styles/classNames";

export interface NavPage {
  readonly path: string;
  readonly label: string;
  /** Active only on exactly this path, not on the paths below it. */
  readonly end?: boolean;
  /** The served decision opening the page needs (SOT-02, GS-8): refused about where things are, it isn't listed; otherwise it shows unavailable, with why. */
  readonly requires?: Decision;
}

interface PageNavProps {
  /** Names the navigation for screen readers. */
  label: string;
  pages: readonly NavPage[];
  /** At the right end, such as a link to the guide. */
  trailing?: ReactNode;
}

const ITEM_CLASS = "app-nav-item px-3 py-1.5 text-sm font-semibold";

/** The pages of one area as links, one route each (UX-29): the profile, a project's settings and Administration. */
export default function PageNav({ label, pages, trailing }: PageNavProps) {
  const { t } = useTranslation();
  return (
    <nav aria-label={label} className="flex flex-wrap items-center gap-2">
      {pages.map((page) => {
        const requirement = page.requires === undefined ? null : requirementOf(t, page.requires);
        if (requirement !== null && !requirement.shown) return null;
        if (requirement?.reason != null) return <UnavailablePage key={page.path} label={page.label} reason={requirement.reason} />;
        return (
          <NavLink key={page.path} to={page.path} end={page.end} className={({ isActive }) => classNames(ITEM_CLASS, isActive && "app-nav-item-active")}>
            {page.label}
          </NavLink>
        );
      })}
      {trailing}
    </nav>
  );
}

/** A page one may not open, in view with why on hover and focus (P-B). */
function UnavailablePage({ label, reason }: { label: string; reason: string }) {
  const tooltip = useAnchoredTooltip();
  const reasonId = useId();
  return (
    <>
      <span role="link" aria-disabled="true" tabIndex={0} aria-describedby={reasonId} className={classNames(ITEM_CLASS, "cursor-not-allowed opacity-50")}
        {...tooltip.hoverProps()} {...tooltip.focusProps()}>
        {label}
      </span>
      <span id={reasonId} className="sr-only">{reason}</span>
      {tooltip.shown === null ? null : <AnchoredTooltip anchor={tooltip.shown.anchor}>{reason}</AnchoredTooltip>}
    </>
  );
}
